import api from './api';

// ============================================
// TYPES - Expanded for EventsCV features
// ============================================

export interface Event {
  id: number;
  entity_id: string | number;
  titulo: string;
  slug?: string;
  descricao?: string;
  descricao_curta?: string;
  categoria?: 'CONCERT' | 'FESTIVAL' | 'CONFERENCE' | 'WORKSHOP' | 'SPORTS' | 'THEATER' | 'EXHIBITION' | 'PARTY' | 'OTHER';
  tipo?: 'PRESENCIAL' | 'ONLINE' | 'HIBRIDO';

  local_nome?: string;
  local_endereco?: string;
  local_cidade?: string;
  local_pais?: string;
  local_latitude?: number;
  local_longitude?: number;
  local_capacidade?: number;

  data_inicio: string;
  data_fim?: string;
  data_abertura_portas?: string;
  timezone?: string;

  imagem_capa?: string;
  imagem_banner?: string;
  imagem_url?: string;
  video_url?: string;
  galeria?: string[];

  status?: 'DRAFT' | 'PUBLISHED' | 'CANCELLED' | 'COMPLETED' | 'POSTPONED' | 'PENDING_REVIEW' | 'REJECTED';
  visibilidade?: 'PUBLIC' | 'PRIVATE' | 'UNLISTED';
  requer_aprovacao?: boolean;
  idade_minima?: number;

  vendas_inicio?: string;
  vendas_fim?: string;
  max_bilhetes_por_compra?: number;

  pass_ativo?: boolean;
  cashless_ativo?: boolean;
  nfc_ativo?: boolean;
  gamificacao_ativa?: boolean;
  chat_ativo?: boolean;
  waitlist_ativa?: boolean;
  beach_club_ativo?: boolean;
  resort_ativo?: boolean;
  documentos_ativo?: boolean;
  sms_ativo?: boolean;
  email_ativo?: boolean;
  stock_ativo?: boolean;

  meta_titulo?: string;
  meta_descricao?: string;
  tags?: string[];

  total_bilhetes_vendidos?: number;
  total_checkins?: number;
  receita_total?: number;

  plano?: 'STARTER' | 'PRO' | 'ENTERPRISE';

  organizador_nome?: string;
  organizador_email?: string;
  organizador_telefone?: string;

  created_at?: string;
  updated_at?: string;
  created_by?: number;

  flag_esgotado?: boolean;

  // Computed fields from queries
  total_tipos_bilhete?: number;
  preco_minimo?: number;
  preco_maximo?: number;
}

export interface EventInput {
  titulo?: string;
  descricao?: string;
  descricao_curta?: string;
  categoria?: string;
  tipo?: string;
  local_nome?: string;
  local_endereco?: string;
  local_cidade?: string;
  local_capacidade?: number;
  local_latitude?: number;
  local_longitude?: number;
  data_inicio?: string;
  data_fim?: string;
  data_abertura_portas?: string;
  imagem_capa?: string;
  imagem_banner?: string;
  galeria?: string[];
  status?: string;
  visibilidade?: string;
  idade_minima?: number;
  vendas_inicio?: string;
  vendas_fim?: string;
  max_bilhetes_por_compra?: number;
  pass_ativo?: boolean;
  cashless_ativo?: boolean;
  nfc_ativo?: boolean;
  gamificacao_ativa?: boolean;
  chat_ativo?: boolean;
  waitlist_ativa?: boolean;
  beach_club_ativo?: boolean;
  resort_ativo?: boolean;
  documentos_ativo?: boolean;
  sms_ativo?: boolean;
  email_ativo?: boolean;
  stock_ativo?: boolean;
  flag_esgotado?: boolean;
  organizador_nome?: string;
  organizador_email?: string;
  organizador_telefone?: string;
  entity_id?: string;
  tags?: string[];
  plano?: string;
}

export interface TicketType {
  id: number;
  evento_id: number;
  nome: string;
  descricao?: string;
  preco: number;
  preco_original?: number;
  moeda?: string;
  quantidade_total: number;
  quantidade_vendida?: number;
  quantidade_reservada?: number;
  quantidade_disponivel?: number;
  venda_inicio?: string;
  venda_fim?: string;
  min_por_compra?: number;
  max_por_compra?: number;
  visibilidade?: 'PUBLIC' | 'HIDDEN' | 'MEMBERS_ONLY';
  codigo_acesso?: string;
  beneficios?: string[];
  zona_acesso?: string;
  transferivel?: boolean;
  reembolsavel?: boolean;
  prazo_reembolso_horas?: number;
  preco_dinamico_ativo?: boolean;
  preco_minimo?: number;
  preco_maximo?: number;
  ordem?: number;
  status?: 'ACTIVE' | 'INACTIVE' | 'SOLD_OUT';
  created_at?: string;
}

export interface Ticket {
  id: number;
  pedido_id: number;
  evento_id: number;
  bilhete_tipo_id: number;
  codigo: string;
  qr_code?: string;
  participante_nome?: string;
  participante_email?: string;
  participante_telefone?: string;
  participante_documento?: string;
  preco: number;
  status?: 'VALID' | 'USED' | 'CANCELLED' | 'TRANSFERRED' | 'EXPIRED';
  checkin_at?: string;
  checkin_by?: number;
  checkin_method?: 'QR' | 'NFC' | 'MANUAL' | 'AUTO';
  checkin_local?: string;
  checkout_at?: string;
  nfc_uid?: string;
  nfc_vinculado_at?: string;
  created_at?: string;
  tipo_nome?: string;
  zona_acesso?: string;
}

export interface Wristband {
  id: number;
  evento_id: number;
  entity_id: string | number;
  nfc_uid: string;
  nfc_serial?: string;
  bilhete_id?: number;
  user_id?: number;
  participante_nome?: string;
  participante_email?: string;
  participante_telefone?: string;
  saldo: number;
  saldo_bonus?: number;
  status?: 'AVAILABLE' | 'ASSIGNED' | 'ACTIVE' | 'BLOCKED' | 'LOST' | 'RETURNED';
  ativada_at?: string;
  bloqueada_at?: string;
  bloqueio_motivo?: string;
  total_carregado?: number;
  total_gasto?: number;
  total_transacoes?: number;
  created_at?: string;
}

export interface CashlessTransaction {
  id: number;
  evento_id: number;
  entity_id: string | number;
  pulseira_id: number;
  tipo: 'TOPUP' | 'PURCHASE' | 'REFUND' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'BONUS' | 'CASHOUT';
  valor: number;
  saldo_anterior: number;
  saldo_posterior: number;
  descricao?: string;
  referencia?: string;
  vendor_id?: number;
  pdv_id?: number;
  operador_id?: number;
  metodo_pagamento?: 'SISP' | 'CASH' | 'WALLET' | 'BONUS';
  pagamento_referencia?: string;
  itens?: any[];
  status?: 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  created_at?: string;
  nfc_uid?: string;
  participante_nome?: string;
}

export interface Vendor {
  id: number;
  evento_id: number;
  entity_id: string | number;
  nome: string;
  descricao?: string;
  categoria?: 'FOOD' | 'DRINKS' | 'MERCHANDISE' | 'SERVICES' | 'OTHER';
  localizacao?: string;
  responsavel_nome?: string;
  responsavel_email?: string;
  responsavel_telefone?: string;
  comissao_percentual?: number;
  logo?: string;
  status?: 'PENDING' | 'APPROVED' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  total_vendas?: number;
  total_transacoes?: number;
  total_produtos?: number;
  created_at?: string;
}

export interface VendorProduct {
  id: number;
  vendor_id: number;
  evento_id: number;
  nome: string;
  descricao?: string;
  categoria?: string;
  preco: number;
  quantidade_disponivel?: number;
  quantidade_vendida?: number;
  imagem?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'SOLD_OUT';
  ordem?: number;
}

export interface Badge {
  id: number;
  evento_id?: number;
  entity_id?: string | number;
  nome: string;
  descricao?: string;
  icone?: string;
  cor?: string;
  tipo: 'CHECKIN' | 'PURCHASE' | 'SOCIAL' | 'CHALLENGE' | 'SPECIAL' | 'TIME' | 'LOCATION';
  criterio?: any;
  pontos_recompensa?: number;
  max_por_utilizador?: number;
  quantidade_total?: number;
  quantidade_distribuida?: number;
  status?: 'ACTIVE' | 'INACTIVE';
}

export interface Challenge {
  id: number;
  evento_id: number;
  entity_id: string | number;
  titulo: string;
  descricao?: string;
  instrucoes?: string;
  tipo?: 'DAILY' | 'WEEKLY' | 'EVENT' | 'SPECIAL';
  categoria?: 'SOCIAL' | 'PURCHASE' | 'EXPLORE' | 'ENGAGE' | 'CUSTOM';
  meta?: any;
  pontos_recompensa?: number;
  badge_recompensa_id?: number;
  badge_recompensa_nome?: string;
  premio_descricao?: string;
  inicio?: string;
  fim?: string;
  max_participantes?: number;
  participantes_atuais?: number;
  completados?: number;
  status?: 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
}

export interface PromoCode {
  id: number;
  evento_id: number;
  entity_id: string | number;
  codigo: string;
  nome?: string;
  descricao?: string;
  tipo_desconto: 'PERCENTAGE' | 'FIXED' | 'FREE_TICKET';
  valor_desconto: number;
  aplica_a?: 'ALL' | 'SPECIFIC_TICKETS' | 'FIRST_PURCHASE';
  bilhete_tipos?: number[];
  max_usos?: number;
  max_usos_por_utilizador?: number;
  usos_atuais?: number;
  valor_minimo_pedido?: number;
  desconto_maximo?: number;
  valido_de?: string;
  valido_ate?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'EXPIRED';
}

export interface Blast {
  id: number;
  evento_id: number;
  entity_id: string | number;
  titulo: string;
  mensagem: string;
  canais?: string[];
  segmento?: 'ALL' | 'TICKET_HOLDERS' | 'CHECKED_IN' | 'NOT_CHECKED_IN' | 'VIP' | 'CUSTOM';
  filtros?: any;
  agendado_para?: string;
  enviado_at?: string;
  total_destinatarios?: number;
  total_enviados?: number;
  total_entregues?: number;
  total_abertos?: number;
  total_clicados?: number;
  status?: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELLED' | 'FAILED';
  created_by?: number;
  created_at?: string;
}

export interface ChatRoom {
  id: number;
  evento_id: number;
  entity_id: string | number;
  nome: string;
  descricao?: string;
  tipo?: 'GENERAL' | 'ANNOUNCEMENTS' | 'SUPPORT' | 'VIP' | 'TOPIC';
  apenas_leitura?: boolean;
  requer_moderacao?: boolean;
  max_mensagens_por_minuto?: number;
  status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  total_mensagens?: number;
  total_membros?: number;
}

export interface WaitlistEntry {
  id: number;
  evento_id: number;
  bilhete_tipo_id?: number;
  tipo_bilhete_nome?: string;
  email: string;
  nome?: string;
  telefone?: string;
  user_id?: number;
  status?: 'WAITING' | 'NOTIFIED' | 'PURCHASED' | 'EXPIRED' | 'CANCELLED';
  notificado_at?: string;
  expira_at?: string;
  posicao?: number;
  quantidade?: number;
  notas?: string;
  created_at?: string;
}

export interface LeaderboardEntry {
  id: number;
  nome?: string;
  avatar?: string;
  pontos: number;
  nivel?: number;
  xp?: number;
  total_checkins?: number;
  total_compras?: number;
  posicao: number;
}

export interface EventStats {
  total_eventos: number;
  eventos_ativos: number;
  total_bilhetes_vendidos: number;
  receita_bilhetes: number;
  total_checkins: number;
  receita_cashless: number;
  needsSetup?: boolean;
}

export interface EventAnalytics {
  bilhetes: {
    vendidos: number;
    checkins: number;
    receita: number;
  };
  cashless: {
    topups: number;
    compras: number;
    saldo_circulante: number;
  };
  gamificacao: {
    participantes: number;
    badges_conquistados: number;
    desafios_completados: number;
  };
  comunicacao: {
    blasts_enviados: number;
    mensagens_chat: number;
  };
}

export interface PaginatedResponse<T> {
  success?: boolean;
  data: T[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages?: number;
  };
  stats?: any;
  needsSetup?: boolean;
}

// ============================================
// SERVICE
// ============================================

class EventsService {
  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{
    success: boolean;
    message: string;
    created: string[];
    totalTables: number;
  }> {
    const response = await api.post('/admin/events/setup');
    return response.data;
  }

  // ============================================
  // STATISTICS
  // ============================================

  async getStats(): Promise<EventStats> {
    const response = await api.get('/admin/events/stats');
    return response.data;
  }

  /**
   * Dispara o crawler de eventos públicos de Cabo Verde (agendacultural.cv,
   * anacao.cv). Insere os novos como DRAFT. Só PFADM.
   */
  async runCrawler(): Promise<{
    success: boolean;
    message: string;
    stats: { found: number; inserted: number; skipped: number; errors: number };
  }> {
    const response = await api.post('/admin/events/crawler/run');
    return response.data;
  }

  /**
   * Semeia a lista curada de eventos futuros reais de Cabo Verde (Out 2026 →
   * 2027). Insere como DRAFT. Só PFADM. Fonte fiável, ao contrário do crawler.
   */
  async seedEvents(): Promise<{
    success: boolean;
    message: string;
    stats: { found: number; inserted: number; skipped: number; errors: number };
  }> {
    const response = await api.post('/admin/events/crawler/seed');
    return response.data;
  }

  /**
   * Importa um evento de Facebook/Instagram a partir de um link público e/ou do
   * texto colado do post. Cria um DRAFT e devolve os campos detetados + avisos.
   * Só PFADM. Compliant — não faz login nem raspa conteúdo privado.
   */
  async importSocial(payload: { url?: string; text?: string }): Promise<{
    success: boolean;
    eventId: number;
    parsed: Record<string, any>;
    warnings: string[];
    message: string;
  }> {
    const response = await api.post('/admin/events/import-social', payload);
    return response.data;
  }

  async getEventAnalytics(eventId: number): Promise<EventAnalytics> {
    const response = await api.get(`/admin/events/${eventId}/analytics`);
    return response.data.data;
  }

  // ============================================
  // EVENTS CRUD
  // ============================================

  async getEvents(params?: {
    page?: number;
    limit?: number;
    status?: string;
    categoria?: string;
    search?: string;
  }): Promise<PaginatedResponse<Event>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.categoria) queryParams.append('categoria', params.categoria);
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get(`/admin/events?${queryParams.toString()}`);
    return response.data;
  }

  async getEvent(id: number): Promise<{ success: boolean; data: Event & { tiposBilhete: TicketType[]; stats: any } }> {
    const response = await api.get(`/admin/events/${id}`);
    return response.data;
  }

  async createEvent(data: EventInput): Promise<{ success: boolean; data: { id: number; slug: string }; message: string }> {
    const response = await api.post('/admin/events', data);
    return response.data;
  }

  async updateEvent(id: number, data: EventInput): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/admin/events/${id}`, data);
    return response.data;
  }

  async deleteEvent(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/events/${id}`);
    return response.data;
  }

  async publishEvent(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/admin/events/${id}/publish`);
    return response.data;
  }

  async updateEventPlan(id: number, plano: string): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/admin/events/${id}`, { plano });
    return response.data;
  }

  // ============================================
  // TICKET TYPES
  // ============================================

  async getTicketTypes(eventId: number): Promise<{ success: boolean; data: TicketType[] }> {
    const response = await api.get(`/admin/events/${eventId}/ticket-types`);
    return response.data;
  }

  async createTicketType(eventId: number, data: Partial<TicketType>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/ticket-types`, data);
    return response.data;
  }

  async updateTicketType(eventId: number, typeId: number, data: Partial<TicketType>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/admin/events/${eventId}/ticket-types/${typeId}`, data);
    return response.data;
  }

  async deleteTicketType(eventId: number, typeId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/events/${eventId}/ticket-types/${typeId}`);
    return response.data;
  }

  // ============================================
  // TICKETS
  // ============================================

  async getTickets(eventId: number, params?: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Ticket>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await api.get(`/admin/events/${eventId}/tickets?${queryParams.toString()}`);
    return response.data;
  }

  async checkInTicket(eventId: number, ticketId: number, data?: { method?: string; local?: string; operador_id?: number }): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/tickets/${ticketId}/checkin`, data || {});
    return response.data;
  }

  async validateTicket(eventId: number, codigo: string): Promise<{ success: boolean; valid: boolean; data: Partial<Ticket> }> {
    const response = await api.get(`/admin/events/${eventId}/validate/${codigo}`);
    return response.data;
  }

  // ============================================
  // VENDORS
  // ============================================

  async getVendors(eventId: number): Promise<{ success: boolean; data: Vendor[] }> {
    const response = await api.get(`/admin/events/${eventId}/vendors`);
    return response.data;
  }

  async createVendor(eventId: number, data: Partial<Vendor>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/vendors`, data);
    return response.data;
  }

  async updateVendor(eventId: number, vendorId: number, data: Partial<Vendor>): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/admin/events/${eventId}/vendors/${vendorId}`, data);
    return response.data;
  }

  async deleteVendor(eventId: number, vendorId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/events/${eventId}/vendors/${vendorId}`);
    return response.data;
  }

  // Vendor Products
  async getVendorProducts(eventId: number, vendorId: number): Promise<{ success: boolean; data: VendorProduct[] }> {
    const response = await api.get(`/admin/events/${eventId}/vendors/${vendorId}/products`);
    return response.data;
  }

  async createVendorProduct(eventId: number, vendorId: number, data: Partial<VendorProduct>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/vendors/${vendorId}/products`, data);
    return response.data;
  }

  // ============================================
  // WRISTBANDS / NFC
  // ============================================

  async getWristbands(eventId: number, params?: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Wristband>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await api.get(`/admin/events/${eventId}/wristbands?${queryParams.toString()}`);
    return response.data;
  }

  async createWristband(eventId: number, data: {
    nfc_uid: string;
    nfc_serial?: string;
    bilhete_id?: number;
    user_id?: number;
    participante_nome?: string;
    participante_email?: string;
    participante_telefone?: string;
    saldo_inicial?: number;
    entity_id?: string | number;
  }): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/wristbands`, data);
    return response.data;
  }

  async getWristbandStats(eventId: number): Promise<{ success: boolean; data: any }> {
    const response = await api.get(`/admin/events/${eventId}/wristbands/stats`);
    return response.data;
  }

  async createWristbandBatch(eventId: number, wristbands: { nfc_uid: string; nfc_serial?: string }[]): Promise<any> {
    const response = await api.post(`/admin/events/${eventId}/wristbands/batch`, { wristbands });
    return response.data;
  }

  async deleteWristband(eventId: number, wristbandId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/admin/events/${eventId}/wristbands/${wristbandId}`);
    return response.data;
  }

  async topUpWristband(eventId: number, wristbandId: number, data: {
    valor: number;
    metodo_pagamento?: string;
    pagamento_referencia?: string;
    operador_id?: number;
    entity_id?: string | number;
  }): Promise<{ success: boolean; message: string; saldo: number }> {
    const response = await api.post(`/admin/events/${eventId}/wristbands/${wristbandId}/topup`, data);
    return response.data;
  }

  // ============================================
  // CASHLESS TRANSACTIONS
  // ============================================

  async getCashlessTransactions(eventId: number, params?: {
    tipo?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<CashlessTransaction>> {
    const queryParams = new URLSearchParams();
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await api.get(`/admin/events/${eventId}/cashless-transactions?${queryParams.toString()}`);
    return response.data;
  }

  // ============================================
  // GAMIFICATION - BADGES
  // ============================================

  async getBadges(eventId: number): Promise<{ success: boolean; data: Badge[] }> {
    const response = await api.get(`/admin/events/${eventId}/badges`);
    return response.data;
  }

  async createBadge(eventId: number, data: Partial<Badge>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/badges`, data);
    return response.data;
  }

  // ============================================
  // GAMIFICATION - CHALLENGES
  // ============================================

  async getChallenges(eventId: number): Promise<{ success: boolean; data: Challenge[] }> {
    const response = await api.get(`/admin/events/${eventId}/challenges`);
    return response.data;
  }

  async createChallenge(eventId: number, data: Partial<Challenge>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/challenges`, data);
    return response.data;
  }

  // ============================================
  // GAMIFICATION - LEADERBOARD
  // ============================================

  async getLeaderboard(eventId: number, params?: { tipo?: string; limit?: number }): Promise<{ success: boolean; data: LeaderboardEntry[] }> {
    const queryParams = new URLSearchParams();
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await api.get(`/admin/events/${eventId}/leaderboard?${queryParams.toString()}`);
    return response.data;
  }

  // ============================================
  // PROMOTIONAL CODES
  // ============================================

  async getPromoCodes(eventId: number): Promise<{ success: boolean; data: PromoCode[] }> {
    const response = await api.get(`/admin/events/${eventId}/promo-codes`);
    return response.data;
  }

  async createPromoCode(eventId: number, data: Partial<PromoCode>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/promo-codes`, data);
    return response.data;
  }

  // ============================================
  // WAITLIST
  // ============================================

  async getWaitlist(eventId: number): Promise<{ success: boolean; data: WaitlistEntry[]; stats: any }> {
    const response = await api.get(`/admin/events/${eventId}/waitlist`);
    return response.data;
  }

  // ============================================
  // BLASTS (Mass Communications)
  // ============================================

  async getBlasts(eventId: number): Promise<{ success: boolean; data: Blast[] }> {
    const response = await api.get(`/admin/events/${eventId}/blasts`);
    return response.data;
  }

  async createBlast(eventId: number, data: Partial<Blast>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/blasts`, data);
    return response.data;
  }

  // ============================================
  // CHAT
  // ============================================

  async getChatRooms(eventId: number): Promise<{ success: boolean; data: ChatRoom[] }> {
    const response = await api.get(`/admin/events/${eventId}/chat/rooms`);
    return response.data;
  }

  async createChatRoom(eventId: number, data: Partial<ChatRoom>): Promise<{ success: boolean; data: { id: number }; message: string }> {
    const response = await api.post(`/admin/events/${eventId}/chat/rooms`, data);
    return response.data;
  }

  // ============================================
  // CHECK-IN LOGS
  // ============================================

  async getCheckinLogs(eventId: number, params?: {
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await api.get(`/admin/events/${eventId}/checkin-logs?${queryParams.toString()}`);
    return response.data;
  }
}

export default new EventsService();
